import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, X, ArrowLeft,
  ChevronLeft, ChevronRight, Search, Download, Paperclip, LayoutList, Table2, ArrowUp, ArrowDown,
  MessageSquare, Unlock, AlertCircle, Shield, Briefcase,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SortableHeader, { PAGE_SIZE } from '../components/SortableTable';
import NowName, { staffNow } from '../components/shared/NowName';
import ResultCount from '../components/shared/ResultCount';
import { formatDate, formatDateShort } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { exportToExcel } from '../utils/excelExport';
import { LEAD_GRADE } from '../utils/staffGrades';
import { Field, FilePicker, inputCls, inputBaseCls } from '../components/shared/FormKit';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import FileList from '../components/shared/FileList';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { DetailItem } from '../components/workflow/SectionCard';
import { detailGridGap } from '../components/workflow/constants';
import { exportExcelBtnCls } from '../components/shared/ExportExcelButton';

const THEAD_CLS = 'px-4 py-2 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap';

// Attachments: optional on every register (server/routes/qm.routes.js).
const fileHref = (type, recordId, fileId) => `${API_BASE_URL}/api/qm/registers/${type}/${recordId}/files/${fileId}`;
const MANAGER_LABEL = 'Engagement Manager/Director';

// Dates arrive as YYYY-MM-DD or full ISO strings — keep the date part only.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

// Financial Year select: NA + audit years back to 2015 (next year first).
// The value column stays free text server-side; an edited legacy value that
// is not in this list is appended as an extra option so it stays visible.
const FY_OPTIONS = ['NA'];
for (let y = new Date().getFullYear() + 1; y >= 2015; y--) FY_OPTIONS.push(String(y));

// Register type configuration — one page, five register resources.
// Field types: date | text | textarea | select (dictionary) | staff (staff master select)
// textarea fields set `rows` to fit the content actually recorded (the
// complaints comments run to ~1300 chars); short fields render two per row
// in the modal, textareas span the full width.
const REGISTER_TABS = [
  {
    key: 'complaints',
    label: 'Complaints',
    icon: MessageSquare,
    noun: 'complaint',
    titleField: 'clientName',
    fields: [
      { key: 'dateReceived', label: 'Date Received', type: 'date', required: true },
      { key: 'clientName', label: 'Client / Engagement', type: 'text', required: true },
      { key: 'financialYear', label: 'Financial Year', type: 'select', options: FY_OPTIONS },
      { key: 'status', label: 'Status', type: 'select', options: ['Open', 'Closed'], required: true },
      { key: 'engagementPartner', label: 'Engagement Partner', type: 'staff', staff: 'partners' },
      { key: 'engagementManager', label: MANAGER_LABEL, type: 'staff', staff: 'managers' },
      { key: 'nature', label: 'Nature', type: 'textarea', rows: 4, required: true },
      { key: 'comments', label: 'Comments', type: 'textarea', rows: 10, placeholder: 'Root cause / resolution status / follow-up actions…' },
      { key: 'partnerReply', label: 'Partner Reply', type: 'textarea', rows: 2, placeholder: 'e.g. ST replied on 13/Oct/2025' },
      { key: 'remarks', label: 'Remarks', type: 'textarea', rows: 3 },
    ],
  },
  {
    key: 'unlocking',
    label: 'Unlocking',
    icon: Unlock,
    noun: 'unlocking request',
    titleField: 'client',
    fields: [
      { key: 'client', label: 'Client', type: 'text', required: true },
      { key: 'financialYearEnd', label: 'Financial Year End', type: 'date' },
      { key: 'dateOfAuditReport', label: 'Date of Audit Report', type: 'date' },
      { key: 'dateOfArchival', label: 'Date of Archival', type: 'date' },
      { key: 'dateOfChanges', label: 'Date of Changes', type: 'date', required: true },
      { key: 'partnerInvolved', label: 'Partner Involved', type: 'staff', staff: 'partners' },
      { key: 'engagementManager', label: MANAGER_LABEL, type: 'staff', staff: 'managers' },
      { key: 'status', label: 'Status', type: 'select', options: ['Pending', 'Approved to unlock file'], required: true },
      { key: 'reasonForChanges', label: 'Reason for Changes', type: 'textarea', rows: 4, required: true },
      { key: 'evidenceOfApproval', label: 'Evidence of Approval', type: 'textarea', rows: 3 },
      { key: 'remarks', label: 'Remarks', type: 'textarea', rows: 3 },
    ],
  },
  {
    key: 'eqr',
    label: 'EQR',
    icon: AlertCircle,
    noun: 'EQR lapse',
    titleField: 'entityInvolved',
    fields: [
      { key: 'dateReported', label: 'Date Reported', type: 'date', required: true },
      { key: 'reportingOfficer', label: 'Reporting Officer', type: 'text' },
      { key: 'engagementPartner', label: 'Engagement Partner', type: 'staff', staff: 'partners' },
      { key: 'engagementManager', label: MANAGER_LABEL, type: 'staff', staff: 'managers' },
      { key: 'entityInvolved', label: 'Entity Involved', type: 'text', required: true },
      { key: 'financialYearEnd', label: 'Financial Year End', type: 'date' },
      { key: 'nature', label: 'Nature', type: 'textarea', rows: 4, required: true },
      { key: 'remarks', label: 'Remarks', type: 'textarea', rows: 3 },
    ],
  },
  {
    key: 'cyber',
    label: 'Cyber',
    icon: Shield,
    noun: 'cyber security incident',
    titleField: 'entityInvolved',
    fields: [
      { key: 'dateReported', label: 'Date Reported', type: 'date', required: true },
      { key: 'reportingOfficer', label: 'Reporting Officer', type: 'text' },
      { key: 'entityInvolved', label: 'Entity Involved', type: 'text', required: true },
      { key: 'nature', label: 'Nature', type: 'textarea', rows: 4, required: true },
      { key: 'remarks', label: 'Remarks', type: 'textarea', rows: 3 },
    ],
  },
  {
    key: 'ac',
    label: 'A&C',
    icon: Briefcase,
    noun: 'A&C lapse',
    titleField: 'entityInvolved',
    fields: [
      { key: 'dateReported', label: 'Date Reported', type: 'date', required: true },
      { key: 'reportingOfficer', label: 'Reporting Officer', type: 'text' },
      { key: 'engagementPartner', label: 'Engagement Partner', type: 'staff', staff: 'partners' },
      { key: 'engagementManager', label: MANAGER_LABEL, type: 'staff', staff: 'managers' },
      { key: 'entityInvolved', label: 'Entity Involved', type: 'text', required: true },
      { key: 'financialYearEnd', label: 'Financial Year End', type: 'date' },
      { key: 'nature', label: 'Nature', type: 'textarea', rows: 4, required: true },
      { key: 'remarks', label: 'Remarks', type: 'textarea', rows: 3 },
    ],
  },
];

const getTabConfig = (key) => REGISTER_TABS.find(t => t.key === key) || REGISTER_TABS[0];

// Modal titles are Title Case; `noun` stays lowercase for use inside sentences.
const titleNoun = (noun) => noun.replace(/\b[a-z]/g, (c) => c.toUpperCase());

// One Excel row per record: every field in full, people by their current name
// (staffNow), then the attachment names.
const excelRow = (tabConfig, r) => {
  const out = {};
  tabConfig.fields.forEach((f) => {
    const v = r[f.key];
    if (f.type === 'date') out[f.label] = v ? formatDate(v) : '';
    else if (f.type === 'staff') out[f.label] = staffNow(v, r[`${f.key}Now`]) || '';
    else out[f.label] = v || '';
  });
  out.Attachments = (r.files || []).map((fl) => fl.originalName).join('; ');
  return out;
};

// Cards (default) or the classic table; remembered per browser.
const VIEW_KEY = 'qmRegisters.view';
const readView = () => {
  try { return localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'cards'; } catch { return 'cards'; }
};
const saveView = (v) => {
  try { localStorage.setItem(VIEW_KEY, v); } catch { /* per-browser convenience only */ }
};

// Labels read as labels: small caps in grey, so they never blend into the text.
const LABEL_CLS = 'text-[11px] font-semibold uppercase tracking-wide text-slate-500';

// Long text shows its first `lines` lines (about 600 characters across half
// a card at 6 lines) with Show More / Show Less when there is more. Short
// entries, the large majority, show whole with no toggle.
const CARD_LINES = 6;
const TABLE_LINES = 4;
const ClampText = ({ text, lines, className = '' }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => { if (!open) setOverflows(el.scrollHeight > el.clientHeight + 1); };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, lines, open]);
  if (!text) return <span className="text-slate-400">—</span>;
  return (
    <div>
      <div
        ref={ref}
        className={`whitespace-pre-wrap break-words ${className}`}
        style={open ? undefined : { display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
      >
        {text}
      </div>
      {(overflows || open) && (
        <button type="button" onClick={() => setOpen(o => !o)} className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-800">
          {open ? 'Show Less' : 'Show More'}
        </button>
      )}
    </div>
  );
};

// Year filter: this year back to 2015, or further when older rows exist.
const yearOptions = (minYear) => {
  const now = new Date().getFullYear();
  const from = Math.min(2015, minYear || now);
  const out = [];
  for (let y = now; y >= from; y--) out.push(String(y));
  return out;
};

const STATUS_DONE = /closed|approved/i;
const StatusBadge = ({ value }) => (value ? (
  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_DONE.test(value) ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
    {value}
  </span>
) : null);

// One field's display value — shared by the cards and the table.
const FieldValue = ({ f, r, short = false }) => {
  if (f.type === 'date') return r[f.key] ? (short ? formatDateShort : formatDate)(r[f.key]) : '—';
  /* 人名是當時的快照——改名過的人印「舊名 (now: 現名)」 */
  if (f.type === 'staff') return <NowName value={r[f.key]} now={r[`${f.key}Now`]} />;
  return r[f.key] || '—';
};

const AttachmentLinks = ({ type, record, className = '' }) => (
  (record.files || []).length === 0 ? <span className="text-slate-400">—</span> : (
    <FileList
      files={record.files}
      divided={false}
      className={className}
      row={(fl) => ({
        name: fl.originalName,
        href: fileHref(type, record.id, fl.id),
        icon: Paperclip,
        pad: 'py-0.5',
        align: 'items-start',
        nameClass: '[overflow-wrap:anywhere]',
      })}
    />
  )
);

const RowActions = ({ onEdit, onDelete }) => (
  <div className="inline-flex gap-1">
    <button onClick={onEdit} title="Edit" className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded">
      <Pencil className="w-4 h-4" />
    </button>
    <button onClick={onDelete} title="Delete" className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded">
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

// A record as a card, read like the engagement detail page: the shared
// DetailItem label chip over a bold value, sections split by a hairline.
// Short fields form a grid; every long text gets its own block, clamped.
const RegisterCard = ({ type, tabConfig, record, onEdit, onDelete }) => {
  const statusField = tabConfig.fields.find(f => f.key === 'status');
  const shortFields = tabConfig.fields.filter(f => f.type !== 'textarea' && f.key !== tabConfig.titleField && f.key !== 'status');
  const longFields = tabConfig.fields.filter(f => f.type === 'textarea');
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="w-1 h-4 rounded-full bg-blue-600 shrink-0" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-slate-900 break-words">{record[tabConfig.titleField] || '—'}</h3>
          {statusField && <StatusBadge value={record.status} />}
        </div>
        <RowActions onEdit={onEdit} onDelete={onDelete} />
      </div>
      <div className={`grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 ${detailGridGap}`}>
        {shortFields.map(f => (
          <DetailItem key={f.key} label={f.label}><FieldValue f={f} r={record} /></DetailItem>
        ))}
      </div>
      {longFields.length > 0 && (
        <div className={`mt-4 border-t border-slate-100 pt-4 grid ${detailGridGap} ${longFields.length > 1 ? 'lg:grid-cols-2' : ''}`}>
          {longFields.map(f => (
            <DetailItem key={f.key} label={f.label}>
              {record[f.key] ? <ClampText text={record[f.key]} lines={CARD_LINES} className="leading-relaxed" /> : null}
            </DetailItem>
          ))}
        </div>
      )}
      <div className="mt-4 border-t border-slate-100 pt-4">
        <DetailItem label="Attachments">
          {(record.files || []).length > 0
            ? <AttachmentLinks type={type} record={record} className="flex flex-wrap gap-x-4 gap-y-1 font-normal" />
            : null}
        </DetailItem>
      </div>
    </div>
  );
};

const QMRegisters = () => {
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab');
  const activeTab = REGISTER_TABS.some(t => t.key === rawTab) ? rawTab : 'complaints';
  const tabConfig = getTabConfig(activeTab);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  // Status + one select per staff field; keys are field keys ('status',
  // 'engagementPartner', …), values the chosen status / current name.
  const [filters, setFilters] = useState({});
  const [filterOpts, setFilterOpts] = useState({ staff: {}, statuses: [], minYear: null });
  const setFilter = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));
  const filterKey = JSON.stringify(filters);
  const [sortConfig, setSortConfig] = useState(null);

  const requestSort = (key) => {
    setPage(1);
    setSortConfig(prev =>
      prev && prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' });
  };

  // Staff master options for the Partner / Manager selects (free text fallback)
  const [staffOpts, setStaffOpts] = useState({ partners: [], managers: [] });
  const [staffOptsLoaded, setStaffOptsLoaded] = useState(false);

  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState(readView);
  const changeView = (v) => { setView(v); saveView(v); };

  const canEdit = hasPermission('qm:registers:edit');

  // Query string shared by the listing and the export.
  const filterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (yearFilter) params.set('year', yearFilter);
    Object.entries(JSON.parse(filterKey)).forEach(([k, v]) => { if (v) params.set(k, v); });
    if (sortConfig?.key) { params.set('sort', sortConfig.key); params.set('dir', sortConfig.direction); }
    return params;
  }, [debouncedSearch, yearFilter, filterKey, sortConfig]);

  // Every row the current search / filters / sort match, not just this page.
  const exportExcel = async () => {
    setExporting(true);
    setError(null);
    try {
      const params = filterParams();
      const data = await fetchJSON(`/api/qm/registers/${activeTab}/export?${params}`);
      exportToExcel((data.data || []).map((r) => excelRow(tabConfig, r)),
        `QM_Register_${tabConfig.label.replace(/[^A-Za-z0-9]+/g, '_')}.xlsx`);
    } catch (e) { setError(e.message); }
    finally { setExporting(false); }
  };

  const switchTab = (key) => {
    setSearchParams({ tab: key });
    setPage(1);
    setSortConfig(null);
    setFilters({});
  };

  // Debounce the search box so we don't refetch on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to first page whenever a filter or the tab changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, yearFilter, filterKey, activeTab]);

  useEffect(() => {
    if (!canEdit) return;
    setFilterOpts({ staff: {}, statuses: [], minYear: null });
    fetchJSON(`/api/qm/registers/${activeTab}/filter-options`)
      .then(d => setFilterOpts({ staff: d.staff || {}, statuses: d.statuses || [], minYear: d.minYear || null }))
      .catch(e => console.error('Error loading filter options:', e));
  }, [canEdit, activeTab]);

  const loadStaffOptions = useCallback(async () => {
    try {
      // The registers' Manager column has always taken Directors as well.
      const [p, m] = await Promise.all([
        fetchJSON(`/api/qm/staff/options?grade=${encodeURIComponent(LEAD_GRADE.partner)}&active=true`),
        fetchJSON(`/api/qm/staff/options?grade=${encodeURIComponent(`${LEAD_GRADE.director},${LEAD_GRADE.manager}`)}&active=true`),
      ]);
      setStaffOpts({
        partners: (p.staff || []).map(s => s.name),
        managers: (m.staff || []).map(s => s.name),
      });
    } catch (e) {
      console.error('Error loading staff options:', e);
    } finally {
      setStaffOptsLoaded(true);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filterParams();
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      const data = await fetchJSON(`/api/qm/registers/${activeTab}?${params}`);
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [activeTab, filterParams, page]);

  useEffect(() => {
    if (!canEdit) return;
    loadStaffOptions();
  }, [canEdit, loadStaffOptions]);

  useEffect(() => {
    if (!canEdit) return;
    load();
  }, [canEdit, load]);

  if (!canEdit) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need register editing rights to access this area.</p>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-4 sm:p-6 mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">QM Registers</h1>
          <p className="text-sm text-slate-500 mt-1">Manage the register data feeding the Complaints, Unlocking, EQR Lapses, Cyber Security and A&amp;C Lapses views.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> New Record
        </button>
      </div>

      {/* Sub-tab bar */}
      <div className="bg-white border-b border-slate-200 px-4 mb-4 rounded-t-lg border border-b-slate-200">
        <div className="flex gap-6 overflow-x-auto">
          {REGISTER_TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => switchTab(t.key)}
                className={`py-3 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === t.key ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 border-b-2 border-transparent hover:text-slate-700 hover:border-slate-200'}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              className={`${inputCls} pl-9`}
            />
          </div>
          {view === 'cards' && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500 font-medium whitespace-nowrap">Sort By</label>
              <select
                value={sortConfig?.key || ''}
                onChange={e => { setPage(1); setSortConfig(e.target.value ? { key: e.target.value, direction: sortConfig?.direction || 'desc' } : null); }}
                className={`${inputBaseCls} w-48`}
              >
                <option value="">Default (Newest First)</option>
                {tabConfig.fields.filter(f => f.type !== 'textarea').map(f => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>
              {sortConfig?.key && (
                <button
                  onClick={() => requestSort(sortConfig.key)}
                  title={sortConfig.direction === 'asc' ? 'Ascending' : 'Descending'}
                  className="p-2 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50"
                >
                  {sortConfig.direction === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                </button>
              )}
            </div>
          )}
          <ResultCount count={total} loading={loading} className="ml-auto" />
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
            {[['cards', 'Cards', LayoutList], ['table', 'Table', Table2]].map(([v, label, Icon]) => (
              <button
                key={v}
                onClick={() => changeView(v)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${view === v ? 'bg-slate-100 text-slate-900' : 'bg-white text-slate-500 hover:text-slate-700'}`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          <button
            onClick={exportExcel}
            disabled={exporting || total === 0}
            className={exportExcelBtnCls}
          >
            <Download className="w-4 h-4" /> {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
        {/* Filters */}
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-end gap-3">
          <div>
            <label className={`block mb-1 ${LABEL_CLS}`}>Year</label>
            <select value={yearFilter} onChange={e => setYearFilter(e.target.value)} className={`${inputBaseCls} w-32`}>
              <option value="">All Years</option>
              {yearOptions(filterOpts.minYear).map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {filterOpts.statuses.length > 0 && (
            <div>
              <label className={`block mb-1 ${LABEL_CLS}`}>Status</label>
              <select value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} className={`${inputBaseCls} w-48`}>
                <option value="">All</option>
                {filterOpts.statuses.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          )}
          {tabConfig.fields.filter(f => f.type === 'staff' || f.key === 'reportingOfficer').map(f => (
            <div key={f.key}>
              <label className={`block mb-1 ${LABEL_CLS}`}>{f.label}</label>
              <select value={filters[f.key] || ''} onChange={e => setFilter(f.key, e.target.value)} className={`${inputBaseCls} w-52`}>
                <option value="">All</option>
                {(filterOpts.staff[f.key] || []).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          ))}
          {(yearFilter || Object.values(filters).some(Boolean)) && (
            <button
              onClick={() => { setYearFilter(''); setFilters({}); }}
              className="pb-2 text-sm text-blue-600 hover:text-blue-800"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">{error}</div>
      )}

      {/* Records: cards (default) or table */}
      <div className={view === 'table' ? 'bg-white rounded-lg border border-slate-200 overflow-hidden' : ''}>
        {view === 'cards' ? (
          <div className="space-y-3 mb-3">
            {loading ? (
              <div className="bg-white rounded-lg border border-slate-200 px-4 py-8 text-center text-sm text-slate-500">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="bg-white rounded-lg border border-slate-200 px-4 py-8 text-center text-sm text-slate-500">No records found</div>
            ) : rows.map(r => (
              <RegisterCard
                key={r.id}
                type={activeTab}
                tabConfig={tabConfig}
                record={r}
                onEdit={() => setEditing(r)}
                onDelete={() => setDeleting(r)}
              />
            ))}
          </div>
        ) : (
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                {tabConfig.fields.map(f => (
                  <SortableHeader key={f.key} label={f.label} sortKey={f.key} sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                ))}
                <SortableHeader label="Attachments" sortable={false} className={THEAD_CLS} />
                <SortableHeader label="Actions" sortable={false} align="right" className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={tabConfig.fields.length + 2} className="px-4 py-8 text-center text-slate-500">Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={tabConfig.fields.length + 2} className="px-4 py-8 text-center text-slate-500">No records found</td>
                </tr>
              ) : rows.map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  {tabConfig.fields.map(f => (
                    <td
                      key={f.key}
                      className={`px-4 py-2 text-slate-700 align-top ${f.type === 'date' ? 'whitespace-nowrap' : ''} ${f.type === 'textarea' ? 'min-w-[260px] max-w-[420px]' : ''}`}
                    >
                      {f.type === 'textarea'
                        ? <ClampText text={r[f.key]} lines={TABLE_LINES} />
                        : <FieldValue f={f} r={r} short />}
                    </td>
                  ))}
                  <td className="px-4 py-2 align-top">
                    <AttachmentLinks type={activeTab} record={r} className="space-y-1 min-w-[220px]" />
                  </td>
                  <td className="px-4 py-2 text-right align-top">
                    <RowActions onEdit={() => setEditing(r)} onDelete={() => setDeleting(r)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        {/* Pagination */}
        <div className={`flex items-center justify-between px-4 py-3 bg-slate-50 text-sm ${view === 'table' ? 'border-t border-slate-200' : 'border border-slate-200 rounded-lg'}`}>
          <span className="text-slate-500">
            {total > 0
              ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`
              : '0 of 0'}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-slate-500">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {editing && (
        <RegisterModal
          type={activeTab}
          tabConfig={tabConfig}
          record={editing}
          staffOpts={staffOpts}
          staffOptsLoaded={staffOptsLoaded}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {showCreate && (
        <RegisterModal
          type={activeTab}
          tabConfig={tabConfig}
          record={null}
          staffOpts={staffOpts}
          staffOptsLoaded={staffOptsLoaded}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {deleting && (
        <DeleteRegisterModal
          type={activeTab}
          tabConfig={tabConfig}
          record={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}
    </div>
  );
};

// Select fed by the staff master with an "Other…" free-text fallback
// (the master may be incomplete — the server still accepts arbitrary text).
const StaffSelectField = ({ value, onChange, options, loaded }) => {
  const [custom, setCustom] = useState(false);

  // Once the staff options arrive, keep a pre-existing value that is not in
  // the master visible by switching to free-text mode.
  useEffect(() => {
    if (loaded && value && !options.includes(value)) setCustom(true);
  }, [loaded, value, options]);

  if (options.length > 0 && !custom) {
    return (
      <select
        value={value}
        onChange={e => {
          if (e.target.value === '__other__') {
            setCustom(true);
            onChange('');
          } else {
            onChange(e.target.value);
          }
        }}
        className={inputCls}
      >
        <option value="">Select…</option>
        {options.map(name => <option key={name} value={name}>{name}</option>)}
        <option value="__other__">Other…</option>
      </select>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        className={inputCls}
      />
      {options.length > 0 && (
        <button
          type="button"
          onClick={() => { setCustom(false); onChange(''); }}
          className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
        >
          Use List
        </button>
      )}
    </div>
  );
};

const RegisterModal = ({ type, tabConfig, record, staffOpts, staffOptsLoaded, onClose, onSaved }) => {
  const isEdit = !!record;

  const initialForm = {};
  tabConfig.fields.forEach(f => {
    const raw = record?.[f.key];
    initialForm[f.key] = f.type === 'date' ? toDateInput(raw) : (raw || '');
  });

  const [form, setForm] = useState(initialForm);
  const [newFiles, setNewFiles] = useState([]);
  const removable = useRemovableFiles();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // 空白的必填格：按 Save 後就地標紅並列進橫幅，填好就消。
  const gaps = useMemo(() => tabConfig.fields
    .filter(f => f.required && !String(form[f.key] || '').trim())
    .map(f => ({ key: f.key, label: f.label })), [form, tabConfig]);
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }

    setSaving(true);
    setError(null);
    // Multipart so the attachments travel with the fields; an empty string
    // clears a field server-side.
    const body = new FormData();
    tabConfig.fields.forEach(f => body.append(f.key, String(form[f.key] || '').trim()));
    newFiles.forEach(file => body.append('files', file));
    if (isEdit) removable.appendTo(body);
    try {
      await fetchJSON(isEdit ? `/api/qm/registers/${type}/${record.id}` : `/api/qm/registers/${type}`, {
        method: isEdit ? 'PATCH' : 'POST',
        body,
      });
      onSaved();
    } catch (e) {
      setError(e.message);
    }
    finally { setSaving(false); }
  };

  const renderField = (f) => {
    switch (f.type) {
      case 'date':
        return (
          <input type="date" value={form[f.key]} onChange={e => setField(f.key, e.target.value)} className={inputCls} />
        );
      case 'textarea':
        return (
          <textarea
            rows={f.rows || 2}
            value={form[f.key]}
            onChange={e => setField(f.key, e.target.value)}
            placeholder={f.placeholder || ''}
            className={inputCls}
          />
        );
      case 'select': {
        // Keep a pre-existing value that is not in the list (e.g. a legacy
        // free-text Financial Year) visible instead of silently dropping it.
        const extra = form[f.key] && !f.options.includes(form[f.key]) ? [form[f.key]] : [];
        return (
          <select value={form[f.key]} onChange={e => setField(f.key, e.target.value)} className={inputCls}>
            <option value="">Select…</option>
            {extra.map(o => <option key={o} value={o}>{o}</option>)}
            {f.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      }
      case 'staff':
        return (
          <StaffSelectField
            value={form[f.key]}
            onChange={v => setField(f.key, v)}
            options={staffOpts[f.staff] || []}
            loaded={staffOptsLoaded}
          />
        );
      default:
        return (
          <input
            type="text"
            value={form[f.key]}
            onChange={e => setField(f.key, e.target.value)}
            placeholder={f.placeholder || ''}
            className={inputCls}
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            {isEdit ? `Edit ${titleNoun(tabConfig.noun)}` : `New ${titleNoun(tabConfig.noun)}`}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <FormValidationProvider value={validation.providerValue}>
          {/* Short fields sit two per row; textareas take the full width. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            {tabConfig.fields.map(f => (
              <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                <Field label={f.label} required={!!f.required} field={f.key}>
                  {renderField(f)}
                </Field>
              </div>
            ))}
            <div className="sm:col-span-2 space-y-2">
              {isEdit && (
                <EditableAttachments
                  files={record.files || []}
                  removeIds={removable.removeIds}
                  onToggle={removable.toggle}
                  downloadHref={(fl) => fileHref(type, record.id, fl.id)}
                  badgeFor={() => [null, '']}
                />
              )}
              <Field label={isEdit && (record.files || []).length > 0 ? 'Add Attachments' : 'Attachments'} field="files">
                <FilePicker multiple kind="registerFiles" onChange={e => setNewFiles(Array.from(e.target.files || []))} />
              </Field>
            </div>
          </div>
          {(error || validation.error) && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <span>{error || validation.error}</span>
            </div>
          )}
          </FormValidationProvider>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button onClick={save} disabled={saving} className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteRegisterModal = ({ type, tabConfig, record, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const confirmDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await fetchJSON(`/api/qm/registers/${type}/${record.id}`, { method: 'DELETE' });
      onDeleted();
    } catch (e) { setError(e.message); }
    finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Delete Record</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            Are you sure you want to delete this {tabConfig.noun}
            {record[tabConfig.titleField] ? (
              <> for <span className="font-semibold">{record[tabConfig.titleField]}</span></>
            ) : ''}? This action cannot be undone.
          </p>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button onClick={confirmDelete} disabled={deleting} className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50">
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QMRegisters;
